import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import axios, { isAxiosError } from 'axios';
import { useAppAuth } from '../hooks/useAppAuth';
import { setDevToken } from '../auth/devSession';

// ====================================================================
//  RegistrationPage
//  Roles: Customer / Carrier / Workshop
// ====================================================================

type Role = 'Customer' | 'Carrier' | 'Workshop';

const api = axios.create({ baseURL: import.meta.env.VITE_API_BASE_URL ?? '' });

export default function RegistrationPage() {
  const { login, isAuthenticated } = useAppAuth();
  const navigate = useNavigate();

  const [role, setRole]               = useState<Role>('Customer');
  const [email, setEmail]             = useState('');
  const [password, setPassword]       = useState('');
  const [firstName, setFirstName]     = useState('');
  const [lastName, setLastName]       = useState('');
  const [gdprConsent, setGdprConsent] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState<string | null>(null);
  const [isDuplicate, setIsDuplicate] = useState(false);

  if (isAuthenticated) return <Navigate to="/" replace />;

  const handleSubmit = async () => {
    setError(null);
    setIsDuplicate(false);
    setSubmitting(true);
    try {
      let endpoint: string;
      let body: Record<string, unknown>;

      if (role === 'Workshop') {
        endpoint = '/api/v1/identity/register/workshop';
        body = {
          email, password,
          contactFirstName: firstName,
          contactLastName:  lastName,
          gdprConsent, termsAccepted
        };
      } else if (role === 'Carrier') {
        endpoint = '/api/v1/identity/register/carrier';
        body = {
          email, password,
          firstName, lastName,
          gdprConsent, termsAccepted
        };
      } else {
        endpoint = '/api/v1/identity/register/customer';
        body = {
          email, password,
          firstName, lastName,
          gdprConsent, termsAccepted
        };
      }

      const res = await api.post(endpoint, body);
      const userId: string = res.data.userId;

      if (import.meta.env.DEV) {
        setDevToken(userId);
        navigate('/profile');
        return;
      }
      login();
    } catch (e: unknown) {
      if (isAxiosError(e)) {
        if (e.response?.status === 409) {
          setIsDuplicate(true);
          setError('An account with this email already exists. Please sign in instead.');
        } else {
          const data = e.response?.data;
          const detail =
            data?.detail ?? data?.message ?? data?.error ??
            (data?.errors ? JSON.stringify(data.errors) : null);
          setError(detail ?? `Something went wrong (${e.response?.status ?? 'unknown'}). Please try again.`);
        }
      } else {
        setError(e instanceof Error ? e.message : 'Registration failed. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <h1 style={styles.h1}>Join TransitDeal</h1>
        <p style={styles.subtitle}>UK's driver-centric delivery & repair marketplace</p>

        {/* ── Role selector ──────────────────────────────────────────── */}
        <div style={styles.roleGrid}>
          {(['Customer', 'Carrier', 'Workshop'] as Role[]).map(r => (
            <button
              key={r}
              style={{ ...styles.roleCard, ...(role === r ? styles.roleCardActive : {}) }}
              onClick={() => setRole(r)}
            >
              <span style={styles.roleIcon}>{roleIcon(r)}</span>
              <strong>{r}</strong>
              <small style={styles.roleDesc}>{roleDesc(r)}</small>
            </button>
          ))}
        </div>

        {/* ── Error banner ───────────────────────────────────────────── */}
        {error && (
          <div style={styles.errorBox}>
            {error}
            {isDuplicate && (
              <button style={{ ...styles.btnPrimary, marginTop: 10 }} onClick={() => login()}>
                Sign In →
              </button>
            )}
          </div>
        )}

        {/* ── Email & Password ───────────────────────────────────────── */}
        <Field label="Email Address *">
          <input
            style={styles.input} type="email" value={email}
            placeholder="you@example.com"
            onChange={e => setEmail(e.target.value)}
          />
        </Field>
        <Field label="Password *">
          <input
            style={styles.input} type="password" value={password}
            placeholder="Minimum 8 characters"
            onChange={e => setPassword(e.target.value)}
          />
        </Field>

        {/* ── Name fields ────────────────────────────────────────────── */}
        <Field label="First Name *">
          <input
            style={styles.input} value={firstName}
            placeholder="First name"
            onChange={e => setFirstName(e.target.value)}
          />
        </Field>
        <Field label="Last Name *">
          <input
            style={styles.input} value={lastName}
            placeholder="Last name"
            onChange={e => setLastName(e.target.value)}
          />
        </Field>

        {/* ── Consents ───────────────────────────────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
          <label style={{ display: 'flex', gap: 8, fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={gdprConsent}
              onChange={e => setGdprConsent(e.target.checked)} />
            I agree to the <a href="/privacy" style={styles.link}>Privacy Policy</a>
          </label>
          <label style={{ display: 'flex', gap: 8, fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={termsAccepted}
              onChange={e => setTermsAccepted(e.target.checked)} />
            I accept the <a href="/terms" style={styles.link}>Terms & Conditions</a>
          </label>
        </div>

        <button
          style={{ ...styles.btnPrimary, marginTop: 20 }}
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? 'Registering…' : `Register as ${role}`}
        </button>

        <p style={styles.loginLink}>
          Already registered? <a href="/login" style={styles.link}>Sign in</a>
        </p>
      </div>
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────────────────

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', marginBottom: 14 }}>
      <label style={{ fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 4 }}>{label}</label>
      {children}
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────

function roleIcon(r: Role) {
  return { Customer: '📦', Carrier: '🚐', Workshop: '🔧' }[r];
}

function roleDesc(r: Role) {
  return {
    Customer: 'Post deliveries, get quotes',
    Carrier:  'Accept delivery jobs, earn money',
    Workshop: 'Bid on vehicle repair jobs',
  }[r];
}

// ── Styles ───────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  page:         { minHeight: '100vh', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  card:         { background: '#fff', borderRadius: 12, padding: 32, width: '100%', maxWidth: 520, boxShadow: '0 4px 24px rgba(0,0,0,0.08)' },
  h1:           { fontSize: 24, fontWeight: 700, margin: '0 0 8px', color: '#111827' },
  subtitle:     { color: '#6b7280', fontSize: 14, marginBottom: 24 },
  roleGrid:     { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 20 },
  roleCard:     { display: 'flex', flexDirection: 'column', gap: 4, padding: 12, border: '2px solid #e5e7eb', borderRadius: 10, cursor: 'pointer', background: '#fff', textAlign: 'left', transition: 'all 0.15s' },
  roleCardActive: { borderColor: '#047857', background: '#ecfdf5' },
  roleIcon:     { fontSize: 22 },
  roleDesc:     { color: '#6b7280', fontSize: 11 },
  input:        { width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' },
  btnPrimary:   { width: '100%', padding: '12px 0', background: '#047857', color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: 'pointer' },
  errorBox:     { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 13, marginBottom: 16 },
  link:         { color: '#047857', textDecoration: 'none' },
  loginLink:    { textAlign: 'center', fontSize: 13, color: '#6b7280', marginTop: 16 },
};
